<template>
  <div
    class="fill-height flex-fill"
  >
    <!-- Filters -->
    <v-container
      fluid
    >
      <v-alert
        class="ma-3"
        text
        type="warning"
        dismissible
        v-if="usedStoragePct >= 80 && usedStoragePct < 100"
      >
        Você atingiu mais de 80% do seu armazenamento total!
        <router-link to="/app/my-account">Verifique aqui o total utilizado.</router-link>
      </v-alert>
      <v-alert
        class="ma-3"
        text
        type="error"
        dismissible
        v-if="usedStoragePct >= 100"
      >
        Você atingiu 100% do seu armazenamento total.
        Exclua processamentos existentes ou entre em contato com o suporte para mais espaço de armazenamento.
      </v-alert>    
      <v-row
        align="baseline"
        justify="center"
        class="ma-0"
      >
        <v-col
          v-if="$vuetify.breakpoint.smAndDown"
          cols="12"
        >
          <v-btn
            fab
            small
            color="accent"
            @click="toggleFilter"
          >
            <v-icon>mdi-magnify</v-icon>
          </v-btn>
        </v-col>
        <v-col
          cols="12"
        >
          <transition name="slide-fade">
            <processing-list-filter
              id="filters"
              v-if="filterActiveAndIsMobile"
              :name-filter.sync="nameFilter"
              :status-filter.sync="statusFilter"
              :processing-per-page.sync="processingPerPage"
              :project-per-page.sync="projectPerPage"
              @order-by-name="orderByName"
              @order-by-date="orderByDate"
              :disabled="this.isLoadingProcessingsFailed"
            >
            </processing-list-filter>
          </transition>
        </v-col>
      </v-row>
    </v-container>
    <!-- Projects -->
    <v-container
      v-if="nameFilterIsEmpty && statusFilterIsEmpty"
      id="projects"
      fluid
    >
      <v-row
        class="ma-0"
      >
        <v-toolbar
          flat
          tile
          dense
          class="transparent"
        >
          <v-btn
            v-if="cdProjectFilter"
            @click="cdProjectFilter = null"
            fab
            x-small
            color="accent"
            class="mr-2"
          >
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <v-toolbar-title
            v-if="!cdProjectFilter"
          >
            Pastas
          </v-toolbar-title>
          <v-toolbar-title
            v-else
          >
            Pasta: {{selectedProjectName}}
          </v-toolbar-title>
          <v-spacer/>
          <project-create-dialog>
            <template v-slot:activator="{ on: dialog }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on: tooltip }">
                  <v-btn
                    id="new-project"
                    icon
                    color="accent"
                    v-on="{...dialog, ...tooltip}"
                  >
                    <v-icon>mdi-folder-plus</v-icon>
                  </v-btn>
                </template>
                <span>Criar nova pasta</span>
              </v-tooltip>
            </template>
          </project-create-dialog>
        </v-toolbar>
        <v-col
          cols="12"
          class="ma-0 pa-0"
        >
          <v-divider/>
        </v-col>
      </v-row>
      <v-row
        v-if="!cdProjectFilter"
        align="start"
        class="ma-0"
      >
        <v-progress-linear
          v-if="isLoadingProjects"
          color="accent"
          rounded
          indeterminate
        >
        </v-progress-linear>
        <empty-state
          v-else-if="isLoadingProjectsFailed"
          class="project-error__alert"
        >
          <template v-slot:icon>
            <v-icon color="grey">mdi-alert-circle</v-icon>
          </template>
          <template v-slot:description>
            <div class="d-flex flex-column text-center">
              <span>Ocorreu um erro durante o carregamentos de seus projetos.</span>
              <span>Por favor tente novamente mais tarde</span>
            </div>
          </template>
        </empty-state>
        <empty-state
          v-else-if="projectListIsEmpty"
        >
          <template v-slot:icon>
            <v-icon color="grey lighten-1">mdi-folder-search</v-icon>
          </template>
          <template v-slot:description>
            Nenhum projeto encontrado
          </template>
        </empty-state>
        <v-col
          v-else
          v-for="project in projectListFilteredPaginated"
          id="projects"
          :key="project['id_project']"
          cols="12"
          sm="6"
          md="4"
          lg="3"
          xl="2"
        >
          <project-folder
            :id="'project-' + project['id_project']"
            :project="project"
            :cd-project.sync="cdProjectFilter"
          />
        </v-col>
      </v-row>
      <v-row
        class="ma-0 mt-2"
      >
        <v-pagination
          v-if="mustShowProjectPagination"
          v-model="projectPage"
          circle
          :length="projectPagesLength"
          :total-visible="12"
        />
      </v-row>      
    </v-container>

    <!-- Processings -->
    <v-container
      id="processings"
      fluid
    >
      <v-row
        class="ma-0"
      >
        <v-toolbar
          flat
          tile
          dense
          class="transparent"
        >
          <v-toolbar-title>Processamentos</v-toolbar-title>
          <v-spacer/>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                v-show="$vuetify.breakpoint.smAndUp"
                @click="onCreateProcessingClick"
                color="accent"
                v-on="on"
              >
                <v-icon>mdi-plus-circle</v-icon>
              </v-btn>
            </template>
            <span>Criar novo processamento</span>
          </v-tooltip>
        </v-toolbar>
        <v-col
          cols="12"
          class="ma-0 pa-0"
        >
          <v-divider/>
        </v-col>
      </v-row>
      <v-row
        id="procs"
        align="start"
        class="ma-0"
      >
        <v-progress-linear
          v-if="isLoadingProcessings"
          color="accent"
          rounded
          indeterminate
        >
        </v-progress-linear>
        <empty-state
          v-else-if="isLoadingProcessingsFailed"
          class="processing-error__alert"
        >
          <template v-slot:icon>
            <v-icon color="grey">mdi-alert-circle</v-icon>
          </template>
          <template v-slot:description>
            <div class="d-flex flex-column text-center">
              <span>Ocorreu um erro durante o carregamentos de seus processamentos.</span>
              <span>Por favor tente novamente mais tarde</span>
            </div>
          </template>
        </empty-state>
        <empty-state
          v-else-if="processingListFilteredListIsEmpty"
        >
          <template v-slot:icon>
            <v-icon color="grey lighten-1">mdi-image-off</v-icon>
          </template>
          <template v-slot:description>
            Nenhum processamento encontrado
          </template>
        </empty-state>
        <transition-group
          tag="div"
          name="flip-list"
          class="flex-fill d-flex flex flex-wrap"
        >
          <v-col
            v-for="(processing, index) in processingListFilteredPaginated"
            :key="processing['id_processing']"
            :id="processing['id_processing']"
            cols="12"
            sm="12"
            md="6"
            lg="4"
            xl="3"
            class="justify-center text-center align-center proc-col"
          >
            <processing-card
              :idx="index"
              :processing="processing"
              @reprocessRequest="refetchData"
            />
          </v-col>
        </transition-group>
      </v-row>
      <v-row
        class="ma-0 mt-2"
      >
        <v-pagination
          v-if="mustShowProcessingsPagination"
          v-model="processingPage"
          circle
          :length="processingPagesLength"
          :total-visible="12"
        />
      </v-row>
    </v-container>
    <!-- <nps class="nps-card" v-bind:show="showNps"/> -->
  </div>
</template>

<script>
  import {cloneDeep, gt, isEmpty, isEqual, isNull} from "lodash/lang"
  import {filter, find, flatMap, includes, orderBy} from "lodash/collection"
  import {mapActions, mapGetters} from "vuex"
  import {
    CLEAN_PROCESSING,
    GET_PROCESSING_LIST,
    LIST_PROCESSING,
    SET_PROCESSING_LIST,
  } from "../../store/modules/processing"
  import {GET_PROJECT_LIST, LIST_PROJECT} from "../../store/modules/project"
  import ProjectFolder from "../../components/Projects/ProjectFolder"
  import ProjectCreateDialog from "../../components/Projects/Configuration/ProjectCreateDialog"
  import moment from "moment"
  import ProcessingCard from "../../components/Processings/ProcessingCard"
  import ProcessingListFilter from "../../components/Processings/ProcessingListFilter"
  import EmptyState from "../../components/Helpers/EmptyState"
  import {NOTIFICATION_TYPES, NotificationOptions} from "../../helpers/notify"
  // import Nps from '../../components/Helpers/Nps.vue'
  // import {checkNpsEvaluation} from "@/api/api-billing"
  import {listSpaceUsed} from "@/api/api-billing"

  export default {
    name: "ProcessingList",
    components: {
      EmptyState,
      ProjectCreateDialog,
      ProjectFolder,
      ProcessingCard,
      ProcessingListFilter,
      // Nps
    },
    data: () => ({
      isLoadingProcessings: false,
      isLoadingProcessingsFailed: false,

      isLoadingProjects: false,
      isLoadingProjectsFailed: false,

      processingPage: 1,

      filterActive: false,

      //  Filter
      statusFilter: [],
      nameFilter: null,
      cdProjectFilter: null,
      processingPerPage: 12,
      projectPage: 1,
      projectPerPage: 12,

      showNps: false,
      usedStoragePct: 0
    }),
    methods: {
      // checkNps() {
      //   const authToken = this.$apiAuth.getAuthToken()
      //   checkNpsEvaluation(authToken)
      //   .then((res) => {
      //     let data = res.data.data
      //     let exist =  data.exist
      //     this.showNps = !exist
      //   })
      //   .catch(() => {
      //     this.showNps = false
      //   })
      // },
      onCreateProcessingClick() {
        this.cleanProcessing()
          .then(() => {
            this.$router.push("/app/processing")
          })
      },

      filterProcessingByName(processingList) {
        if (!isEmpty(this.statusFilter) && isEmpty(this.nameFilter))
          return processingList
        if (isEmpty(this.nameFilter))
          return filter(processingList, proc => isEqual(proc["cd_project"], this.cdProjectFilter))
        this.processingPage = 1
        return filter(processingList,
          proc => includes(proc["nm_processing"].toUpperCase(), this.nameFilter.toUpperCase()))
      },
      filterProcessingByStatus(processingList) {
        if (isEmpty(this.statusFilter) && !isEmpty(this.nameFilter))
          return processingList
        if (isEmpty(this.statusFilter))
          return filter(processingList, proc => isEqual(proc["cd_project"], this.cdProjectFilter))
        this.processingPage = 1
        return filter(processingList, proc => includes(flatMap(this.statusFilter), proc["ab_processing_status"]))
      },

      orderByName(order) {
        this._processingList = orderBy(this._processingList, ["nm_processing"], [order])
      },

      orderByDate(order) {
        this._processingList = orderBy(this._processingList, proc => moment(proc["dh_created"]), [order])
      },

      // Setup
      onCreated() {
        this.isLoadingProcessings = true
        this.isLoadingProjects = true
        // this.checkNps()

        this.$appMng.setTitle("Meus Processamentos")

        this._listProject()
          .then(() => {
            this.isLoadingProjects = false
          }).catch(() => {
          this.isLoadingProjects = false
          this.isLoadingProjectsFailed = true
        })

        this._listProcessing()
          .then(() => {
            this.isLoadingProcessings = false
            this.orderByDate("desc")
          }).catch(() => {
          this.isLoadingProcessings = false
          this.isLoadingProcessingsFailed = true
        })

        listSpaceUsed(this.$apiAuth.getAuthToken())
          .then(res => {
            const usedSpace = res.data.data
            this.usedStoragePct = usedSpace.percentage_used
          })
      },

      toggleFilter() {
        this.filterActive = !this.filterActive
      },

      ...mapActions({
        _listProject: LIST_PROJECT,
        _listProcessing: LIST_PROCESSING,
        cleanProcessing: CLEAN_PROCESSING,
      }),
      refetchData(index){
        this._processingList[index]["ab_processing_status"] = 'REPROCESS_REQUESTED'
        this.$notify(new NotificationOptions({
          message: "Reprocessamento solicitado. Em breve um dos nossos especialistas analisará seu processamento",
          type: NOTIFICATION_TYPES.SUCCESS,
        }))
      }
    },
    computed: {
      filterActiveAndIsMobile() {
        if (this.$vuetify.breakpoint.smAndDown)
          return this.filterActive
        return true
      },
      projectListFilteredPaginated() {
        const filteredProject = cloneDeep(this._projectList)
        return filteredProject.splice(((this.projectPage - 1) * this.projectPerPage), this.projectPerPage)
      },      
      processingListFiltered() {
        return this.filterProcessingByStatus(this.filterProcessingByName(this._processingList))
      },
      processingListFilteredPaginated() {
        const filteredProcessings = cloneDeep(this.processingListFiltered)
        const orderedFilteredProcessings = filteredProcessings.sort(function(a, b) {
          var keyA = new Date(a.dh_created), keyB = new Date(b.dh_created);
          if (keyA < keyB) return 1;
          if (keyA > keyB) return -1;
          return 0;
        });
        return orderedFilteredProcessings.splice(((this.processingPage - 1) * this.processingPerPage), this.processingPerPage)
      },
      processingPagesLength() {
        if (!this.processingListFiltered) return 0
        return Math.ceil(this.processingListFiltered.length / this.processingPerPage)
      },
      projectPagesLength() {
        if (!this._projectList) return 0
        return Math.ceil(this._projectList.length / this.projectPerPage)
      },      
      mustShowProcessingsPagination() {
        return gt(this.processingPagesLength, 1)
      },
      mustShowProjectPagination() {
        return gt(this.projectPagesLength, 1)
      },      
      nameFilterIsEmpty() {
        if (isNull(this.nameFilter))
          return true
        return this.nameFilter === ""
      },
      statusFilterIsEmpty() {
        if (isNull(this.statusFilter))
          return true
        return isEmpty(this.statusFilter)
      },
      selectedProjectName() {
        if (isEmpty(this._projectList))
          return null
        const project = find(
          this._projectList,
          ["id_project", this.cdProjectFilter],
        )
        return project["nm_project"]
      },
      projectListIsEmpty() {
        return isEmpty(this._projectList)
      },
      processingListFilteredListIsEmpty() {
        return isEmpty(this.processingListFiltered)
      },
      _processingList: {
        get() {
          return this.$store.getters[GET_PROCESSING_LIST]
        },
        set(value) {
          this.$store.dispatch(SET_PROCESSING_LIST, value)
        },
      },
      ...mapGetters({
        _projectList: GET_PROJECT_LIST,
      }),
    },
    created() {
      this.onCreated()
    }
  }
</script>

<style scoped lang="scss">
  .ghost {
    opacity: 0.5;
    background: #c8ebfb;
  }

  .slide-fade-enter-active {
    transition: all .3s ease;
  }

  .slide-fade-leave-active {
    transition: all .3s cubic-bezier(1.0, 0.5, 0.8, 1.0);
  }

  .slide-fade-enter, .slide-fade-leave-to
    /* .slide-fade-leave-active below version 2.1.8 */
  {
    transform: translateX(-10px);
    opacity: 0;
  }

  .flip-list-enter-active, .flip-list-leave-active {
    transition: all 1s;
  }

  .flip-list-enter, .flip-list-leave-to /* .fade-leave-active below version 2.1.8 */
  {
    opacity: 0;
    transform: translateY(30px);
  }

  .flip-list-move {
    transition: transform 1s;
  }

  .flip-list-leave-active {
    position: absolute;
  }

  .project-error__alert {
    .v-icon {
      font-size: 120px !important;
    }
  }

  .processing-error__alert {
    .v-icon {
      font-size: 120px !important;
    }
  }

  .nps-card {
    position: fixed;
    z-index: 9999999;
    right: 0; 
    bottom: 0;
    // width: 290px;
    margin-right: 10px !important;
    margin-bottom: 10px !important;
  }
</style>
